import { useCallback, useEffect, useMemo, useState } from "react";

// React-Query
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Redux
import { useSelector } from "react-redux";

// MUI
import { Stack, Typography } from "@mui/material";

// Third Party
import moment from "moment";
import { compact, debounce } from "lodash-es";

// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Services
import { useSalesLogChart } from "@DataPages/context/salesOperations/salesLog/SalesLogChartsContext";
import { getCommissionCycles } from "@Common/api/commissionCycles";

// Components
import SalesLogAnalyticsSkeleton from "@DataPages/components/SalesOperations/Tabs/SalesLog/SalesLogAnalyticsSkeleton";
import SalesLogAnalytics from "./SalesLogAnalytics";
import MultiSelectFilterButtons from "@Layouts/components/FloatingTabButtonSelector/MultiSelectFilterButtons";

// Helpers
import { getAnalytics } from "@DataPages/components/SalesOperations/Tabs/SalesLog/SalesLogAnalyticsHelpers.ts";
import { calculateDynamicPVRInfo, getTabAnalytics, getTabDynamicPVRInfo } from "@DataPages/helpers/salesOperations/salesLog/utils";
import { or } from "@/Common/helpers/sonar";

// Types
import { RootState } from "@/redux/store";
import {
	IAnalytics,
	IDynamicPVRInfo,
	IFranchiseSalesLog,
	ISalesAnalyticsItem,
	SalesLogAnalytic,
} from "@DataPages/types/salesOperations/salesLog/model";
import { SalesLogModelProps } from "@DataPages/types/salesOperations/salesLog/utils";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";
import { ICommissionCycle } from "@Common/api/types/CommissionCycle";
import { getSalesOperationName } from "@DataPages/components/SalesOperations/constants";

// Constants
import { TAB_NAMES } from "@/DataPages/constants/sales-operations";

// Constants
const debouncedTime = 300;

/********************************************** HELPERS *********************************************/
const getNewVehiclesTabNames = (newVehiclesFranchises: IFranchiseSalesLog[] | undefined = []): string[] => {
	if (newVehiclesFranchises.length === 1) {
		return ["New Vehicles"];
	}
	return newVehiclesFranchises?.map((franchise) => franchise.franchiseName) || [];
};

export function isPreOwned(type: string) {
	return type === "USED";
}

const SALES_LOG_ANALYTICS_QUERY_KEY = getSalesOperationName("QUERY", "SALES_LOG_ANALYTICS");
const ALL_COMMISSION_CYCLE_QUERY_KEYS = getSalesOperationName("QUERY", "SO_ALL_COMMISSION_CYCLES");
const SALES_LOG_ANALYTICS_SUBSCRIPTION_KEY = getSalesOperationName("SUBSCRIPTION", "SALES_LOG_ANALYTICS");

export default function SalesLogAnalyticsSection(props: Readonly<SalesLogModelProps>) {
	const { salesLogs, salesLogAnalytics } = props;

	// Hooks
	const { getSubscription } = useSubscriptions();
	const queryClient = useQueryClient();

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId;

	// State
	const [filteredAnalytics, setFilteredAnalytics] = useState<ISalesAnalyticsItem[]>([]);

	const { tabSelected, filteredRows, shouldReCalculateAnalytics, setTabSelected, setOutdatedAnalytics } = useSalesLogChart();

	/***************************************** QUERIES ********************************************/
	const { data: commissionCycles, isLoading: isLoadingAllCommissionCycles } = useQuery({
		queryKey: [ALL_COMMISSION_CYCLE_QUERY_KEYS, locationId],
		queryFn: async () => {
			const result = await getCommissionCycles(locationId as string);
			return result?.sort((a, b) => {
				const dateA = moment(a.startDate);
				const dateB = moment(b.startDate);
				return dateB.diff(dateA);
			});
		},
		staleTime: Infinity,
		enabled: Boolean(locationId),
	});
	const lastActiveCommissionCycle = commissionCycles?.[0];

	const salesLogData = useMemo(() => {
		if (!salesLogAnalytics) {
			return {} as SalesLogAnalytic;
		}

		return {
			...salesLogAnalytics,
			analytics: {
				...salesLogAnalytics.analytics,
				preOwned: {
					...salesLogAnalytics.analytics.preOwned,
				},
				wholesale: {
					...salesLogAnalytics.analytics.wholesale,
				},
				new: salesLogAnalytics.analytics.new.map((newItem: IFranchiseSalesLog) => ({
					...newItem,
				})),
			},
		};
	}, [salesLogAnalytics]);

	const franchiseTabs = useMemo(() => {
		if (salesLogAnalytics) {
			const newVehicleTabs = getNewVehiclesTabNames(salesLogAnalytics.analytics?.new);
			const preOwnedTab = salesLogAnalytics?.analytics?.preOwned ? TAB_NAMES.PRE_OWNED_VEHICLES : "";
			const wholesale = salesLogAnalytics?.analytics?.wholesale ? TAB_NAMES.WHOLESALE : "";
			return compact([...newVehicleTabs, preOwnedTab, wholesale, TAB_NAMES.FI, TAB_NAMES.COMBINED]);
		}
		return [];
	}, [salesLogAnalytics]);
	const defaultTab = franchiseTabs[0];

	/************************************* SUBSCRIPTIONS ********************************************/
	const salesLogAnalyticsSubscription = getSubscription([SALES_LOG_ANALYTICS_SUBSCRIPTION_KEY, props.selectedCommissionCycle?._id]);
	salesLogAnalyticsSubscription?.subscriptions?.onCalculateSalesLogAnalytics?.setCallback(
		(result: GraphqlSubscriptionResponse<"onCalculateSalesLogAnalytics", SalesLogAnalytic>) => {
			const updatedSalesLogAnalytics = result.data?.onCalculateSalesLogAnalytics;
			if (updatedSalesLogAnalytics) {
				queryClient.setQueryData([SALES_LOG_ANALYTICS_QUERY_KEY, locationId, props.selectedCommissionCycle?._id], (_cachedData) => {
					return updatedSalesLogAnalytics;
				});
			}
		}
	);

	const debouncedGetMemoizedAnalytics = useCallback(
		debounce(() => {
			if (shouldReCalculateAnalytics) {
				const filteredTabSales = !filteredRows.length
					? []
					: salesLogs.filter((sale) => {
							return filteredRows.includes(sale._id);
					  });

				const processedAnalytics = getAnalytics(moment(props.selectedCommissionCycle?.startDate), filteredTabSales);

				let analyticsObj;
				switch (tabSelected) {
					case TAB_NAMES.FI:
					case TAB_NAMES.COMBINED:
						analyticsObj = { totals: { salesMetrics: processedAnalytics } } as IAnalytics;
						break;
					case TAB_NAMES.PRE_OWNED_VEHICLES:
						analyticsObj = { preOwned: { salesMetrics: processedAnalytics } } as IAnalytics;
						break;
					case TAB_NAMES.WHOLESALE:
						analyticsObj = { wholesale: { salesMetrics: processedAnalytics } } as IAnalytics;
						break;
					default:
						analyticsObj = {
							new: [{ franchiseName: tabSelected, salesMetrics: processedAnalytics }],
						} as IAnalytics;
						break;
				}

				const resultingValue = getTabAnalytics(tabSelected, analyticsObj);
				setFilteredAnalytics(resultingValue);
			}
		}, debouncedTime),
		[tabSelected, props.selectedCommissionCycle?.startDate, salesLogs, shouldReCalculateAnalytics, filteredRows]
	);

	const { analytics, dynamicPVRInfo } = useMemo(() => {
		if (salesLogData) {
			if (shouldReCalculateAnalytics) {
				return {
					analytics: filteredAnalytics,
					dynamicPVRInfo: calculateDynamicPVRInfo(filteredAnalytics, tabSelected),
				};
			}
			return {
				analytics: getTabAnalytics(tabSelected, salesLogData.analytics),
				dynamicPVRInfo: getTabDynamicPVRInfo(tabSelected, salesLogData.dynamicPVRInfo),
			};
		}
		return { analytics: [], dynamicPVRInfo: {} };
	}, [salesLogData, shouldReCalculateAnalytics, filteredAnalytics, tabSelected]);

	useEffect(() => {
		debouncedGetMemoizedAnalytics();
		return () => {
			debouncedGetMemoizedAnalytics.cancel();
		};
	}, [debouncedGetMemoizedAnalytics]);

	useEffect(() => {
		if (!tabSelected) {
			setTabSelected(defaultTab);
		}
	}, [franchiseTabs, tabSelected]);

	useEffect(() => {
		if (!salesLogAnalytics?.updatedAt || !salesLogs?.length) {
			return;
		}

		const { updatedAt: updatedAtAnalytics } = salesLogAnalytics as SalesLogAnalytic;
		const lastUpdatedAtSalesLog = salesLogs.reduce((max, sale) => {
			const time = new Date(sale.updatedAt).getTime();
			return time > max ? time : max;
		}, -Infinity);

		if (new Date(updatedAtAnalytics).getTime() < lastUpdatedAtSalesLog) {
			/* Re-calculate analytics in the client */
			setOutdatedAnalytics(true);
			return;
		}

		setOutdatedAnalytics(false);
	}, [salesLogAnalytics, salesLogs]);

	// Loading State
	if (or(props.isLoading, isLoadingAllCommissionCycles)) {
		return <SalesLogAnalyticsSkeleton />;
	}

	// No Data State
	if (!props?.selectedCommissionCycle || !lastActiveCommissionCycle) {
		return null;
	}

	return (
		<Stack component={"section"} pt={4} pb={6} spacing={4}>
			{/* Analytics Section Title */}
			{props?.selectedCommissionCycle?.title && (
				<Typography component={"h2"} variant={"h2"}>{`${props?.selectedCommissionCycle?.title} Analytics`}</Typography>
			)}
			<Stack rowGap={2} alignItems={"center"} id={"analytics-grouping"}>
				<MultiSelectFilterButtons
					filterOptions={franchiseTabs}
					selectedTab={tabSelected ?? defaultTab}
					onChangeTab={(selectedTab) => setTabSelected(selectedTab)}
				/>
				{/* Analytics Widgets (Speedometer, Advanced Charts, ...)*/}
				<SalesLogAnalytics
					tabs={franchiseTabs}
					selectedTab={tabSelected ?? defaultTab}
					isLoading={or(props.isLoading, isLoadingAllCommissionCycles)}
					selectedCommissionCycle={props?.selectedCommissionCycle}
					lastActiveCommissionCycle={lastActiveCommissionCycle as ICommissionCycle}
					maxPvrValues={salesLogData?.pvrMaxGraphValues}
					data={analytics}
					dynamicPVRInfo={dynamicPVRInfo as IDynamicPVRInfo}
				/>
			</Stack>
		</Stack>
	);
}
